import { useEffect, useMemo } from "react";
import { useInfiniteQuery, useQuery } from "react-query";
// Shop-link / collection lookups are reads — session-free for public AbortError safety.
import { supabasePublic as supabase } from "../../../config/supabaseClient";
import {
  getComponentsTableName,
  WEBSITE_TEMPLATE_SURFACE,
} from "../../../Utils/StayUpToDate/componentSurface";
import { pickFirstNonEmptyImageField } from "../../../Utils/StayUpToDate/RenderComponent";
import {
  normalizeShopUrl,
  resolvePostShopUrl,
  resolveShopButtonCta,
} from "../../../Utils/normalizeShopUrl";
import {
  isLikelyVideoUrl,
  resolvePlayableVideoUrl,
} from "../../../Utils/media/feedMedia";
import { sanitizePublicCardTitle } from "../../../Utils/media/feedCardTitle";
import { StaticVariables } from "../../../Utils/StaticVariables";
import { useLoadCustomContent } from "../../../Utils/customHooks/UpToDateContent/useLoadCustomContent";
import { normalizeCustomContentPostIds } from "../../../Utils/customHooks/UpToDateContent/normalizeCustomContentPostIds";
import { useLoadTypeContentData } from "../../../Utils/customHooks/UpToDateContent/useLoadTypeContent";
import { getContentFeedDateWindowFromComponent } from "../StayUpToDate/AddComponentPicker/contentFeedTimeframe";
import { useLoadTrendingProducts } from "../../../Utils/customHooks/UpToDateContent/useLoadTrendingProducts";
import { trendingClickSource } from "../../../Utils/customHooks/UpToDateContent/trendingDailySnapshot";
import { useLoadYesterdayLinks } from "../../../Utils/customHooks/UpToDateContent/useLoadYesterdayLinks";
import { useLoadYesterdayContent } from "../../../Utils/customHooks/UpToDateContent/useLoadYesterdayContent";
import { useLoadCollectionComponents } from "../StayUpToDate/StayUpToDateControlsComponents/CollectionComponent/useLoadCollectionComponents";
import { useLoadQueryFeed } from "../LandingPageCarousels/QueryBasedCarousel/useLoadQueryFeed";
import {
  deriveAllowedPlatforms,
  readPlatformFilterFromDetails,
  type ShowFilter,
} from "../LandingPageCarousels/QueryBasedCarousel/filterUtils";
import { fetchStayUpToDateComponentRow } from "../../../components/StayUpToDateComponent/StayUpToDateRenderComponent/useLoadRenderComponent";
import type { ShopCatalogContent, ShopCatalogProduct } from "./templateShopCatalog";
import {
  fetchTemplateShopProductsByIds,
  fetchTemplateCreatorLooksByIds,
  fetchTemplateCreatorLooksRpcPage,
  mapFeedPostToShopCatalogContent,
  TEMPLATE_SHOP_PAGE_SIZE,
} from "./templateShopCatalog";
import { parseProductBreadcrumbs } from "./shopBreadcrumbs";

/** Stable empties so disabled data hooks don't thrash on new `[]` each render. */
const EMPTY_POST_IDS: string[] = [];
const EMPTY_DAY_NUMBERS: number[] = [];
const EMPTY_CATALOG_CONTENT: ShopCatalogContent[] = [];

/** How Featured Outfit / Recent Looks lay out on the page. */
export type LooksDisplayPattern = "content-band" | "sticky-scroll";

/** Products shown beside each reel in the content-band (mockup PRODUCTS). */
export type LooksTagsBeside = "auto" | "0" | "2" | "3" | "4";

export function normalizeLooksDisplayPattern(
  raw: unknown,
  fallback: LooksDisplayPattern,
): LooksDisplayPattern {
  const v = String(raw || "")
    .trim()
    .toLowerCase();
  if (v === "content-band" || v === "band" || v === "drawer") return "content-band";
  if (v === "sticky-scroll" || v === "sticky" || v === "scroll") {
    return "sticky-scroll";
  }
  return fallback;
}

export function normalizeLooksTagsBeside(raw: unknown): LooksTagsBeside {
  const v = String(raw || "")
    .trim()
    .toLowerCase();
  if (v === "0" || v === "none") return "0";
  if (v === "2") return "2";
  if (v === "3") return "3";
  if (v === "4") return "4";
  return "auto";
}

export function normalizeLooksSlotCount(raw: unknown, fallback = 2): 1 | 2 | 3 | 4 {
  const n = Math.round(Number(raw));
  if (n === 1 || n === 2 || n === 3 || n === 4) return n;
  const f = Math.round(Number(fallback));
  if (f === 1 || f === 2 || f === 3 || f === 4) return f;
  return 2;
}

export function productBrandLabel(product: ShopCatalogProduct): string {
  const brand = String(product.breadcrumbs?.brand || "").trim();
  if (brand) return brand;
  return "";
}

const RESERVED_SLUGS = new Set([
  "shop",
  "blog",
  "content",
  "website",
  "dashboard",
  "preview",
  "link-in-bio",
  "linkinbio",
  "products",
  "about",
  "contact",
  "search",
  "platform",
  "trending",
  "category",
]);

const BROWSABLE_HOST_SUFFIXES = [
  "browsable.ai",
  "browsable.bio",
  "searchbubble.com",
];

/** Instagram Content-page looks: FEED (incl. POST) or REELS only. */
const INSTAGRAM_LOOK_TYPES = new Set(["FEED", "REELS", "POST"]);

export type CreatorCollectionRow = {
  id: string;
  type: string;
  postIds: string[];
  uniqueId: string;
  routeName: string;
  name: string;
  slugs: string[];
};

export type LookShopKind = "collection" | "browsable" | "external";

export type CreatorLook = {
  post: ShopCatalogContent;
  collection: CreatorCollectionRow | null;
  products: ShopCatalogProduct[];
  shopKind: LookShopKind;
};

/** @deprecated Prefer CreatorLook — kept for older call sites. */
export type CollectionLook = CreatorLook;

function splitIds(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map((v) => String(v || "").trim()).filter(Boolean);
  }
  const text = String(raw || "").trim();
  if (!text) return [];
  if (
    (text.startsWith("[") && text.endsWith("]")) ||
    (text.startsWith("{") && text.endsWith("}"))
  ) {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) {
        return parsed.map((v) => String(v || "").trim()).filter(Boolean);
      }
      if (parsed && typeof parsed === "object") {
        return Object.values(parsed)
          .map((v) => String(v || "").trim())
          .filter(Boolean);
      }
    } catch {
      /* fall through */
    }
  }
  return text
    .split(",")
    .map((v) => v.trim().replace(/^["']|["']$/g, ""))
    .filter(Boolean);
}

function collectionSlugsFromRow(row: {
  unique_id?: unknown;
  route_name?: unknown;
  name?: unknown;
}): string[] {
  const slugs = new Set<string>();
  for (const raw of [row.unique_id, row.route_name, row.name]) {
    const value = String(raw || "").trim().replace(/^\/+|\/+$/g, "");
    if (!value) continue;
    const last = value.split("/").filter(Boolean).pop() || value;
    const slug = last.trim().toLowerCase();
    if (slug && !RESERVED_SLUGS.has(slug)) slugs.add(slug);
  }
  return [...slugs];
}

function mapCollectionRow(row: Record<string, unknown>): CreatorCollectionRow | null {
  const id = String(row.id ?? "").trim();
  if (!id) return null;
  const slugs = collectionSlugsFromRow(row);
  if (!slugs.length) return null;
  return {
    id,
    type: String(row.type ?? "").trim().toLowerCase(),
    postIds: splitIds(row.post_ids),
    uniqueId: String(row.unique_id ?? "").trim(),
    routeName: String(row.route_name ?? "").trim(),
    name: String(row.display_name ?? row.name ?? "").trim(),
    slugs,
  };
}

export function collectionSlugFromShopUrl(
  url: string,
  username?: string,
): string | null {
  const raw = String(url || "").trim();
  if (!raw) return null;
  const user = String(username || "").trim().toLowerCase();
  try {
    const parsed = new URL(raw, "https://browsable.ai");
    const parts = parsed.pathname.split("/").filter(Boolean);
    if (!parts.length) return null;
    const last = decodeURIComponent(parts[parts.length - 1] || "").trim().toLowerCase();
    if (!last || RESERVED_SLUGS.has(last) || last === user) return null;
    if (parts.length >= 2) {
      const prev = decodeURIComponent(parts[parts.length - 2] || "").trim().toLowerCase();
      if (prev === "shop" || prev === "blog" || prev === "content") return null;
    }
    return last;
  } catch {
    const parts = raw.split("/").filter(Boolean);
    const last = (parts[parts.length - 1] || "").split("?")[0].trim().toLowerCase();
    if (!last || RESERVED_SLUGS.has(last) || last === user) return null;
    return last;
  }
}

function shopUrlMatchesCollection(
  shopUrl: string,
  collection: CreatorCollectionRow,
  username: string,
): boolean {
  const url = String(shopUrl || "").trim().toLowerCase();
  if (!url) return false;
  const slug = collectionSlugFromShopUrl(shopUrl, username);
  if (slug && collection.slugs.includes(slug)) return true;
  return collection.slugs.some((s) => url.includes(`/${s}`));
}

function hostnameOf(url: string): string {
  try {
    return new URL(url, "https://browsable.ai").hostname.toLowerCase();
  } catch {
    return "";
  }
}

function normalizeCustomDomainHost(customDomain?: string | null): string {
  return String(customDomain || "")
    .trim()
    .replace(/^https?:?\/\//i, "")
    .replace(/\/+$/, "")
    .split("/")[0]
    .toLowerCase();
}

function customDomainHostList(
  customDomains?: string | string[] | null,
): string[] {
  const raw = Array.isArray(customDomains)
    ? customDomains
    : customDomains
      ? [customDomains]
      : [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const value of raw) {
    const host = normalizeCustomDomainHost(value);
    if (!host || seen.has(host)) continue;
    seen.add(host);
    out.push(host);
  }
  return out;
}

/** Hosts from `domain_mappings` for this creator (every mapped custom domain). */
export async function fetchCreatorMappedDomainHosts(args: {
  username?: string;
  userId?: string;
}): Promise<string[]> {
  const username = String(args.username || "").trim().toLowerCase();
  const userId = String(args.userId || "").trim();
  if (!username && !userId) return [];
  const hosts: string[] = [];
  const seen = new Set<string>();
  const collect = (rows: { domain?: unknown }[] | null | undefined) => {
    for (const row of rows || []) {
      const host = normalizeCustomDomainHost(String(row.domain || ""));
      if (!host || seen.has(host)) continue;
      seen.add(host);
      hosts.push(host);
    }
  };
  if (username) {
    const { data } = await supabase
      .from("domain_mappings")
      .select("domain")
      .eq("slug", username)
      .limit(50);
    collect(data as { domain?: unknown }[] | null);
  }
  if (userId) {
    const { data } = await supabase
      .from("domain_mappings")
      .select("domain")
      .eq("user_id", userId)
      .limit(50);
    collect(data as { domain?: unknown }[] | null);
  }
  return hosts;
}

/**
 * True when the shop URL is on our site: browsable / searchbubble / creator
 * subdomain / any custom domain in `domain_mappings` (or a relative creator path).
 */
export function isBrowsableShopUrl(
  url: string,
  username?: string,
  customDomains?: string | string[] | null,
): boolean {
  const raw = String(url || "").trim();
  if (!raw) return false;
  const host = hostnameOf(raw);
  if (!host) {
    // Relative creator path e.g. /username/fall-favorites
    return raw.startsWith("/") || Boolean(collectionSlugFromShopUrl(raw, username));
  }
  if (host === "localhost" || host.endsWith(".localhost") || host === "127.0.0.1") {
    return true;
  }
  if (BROWSABLE_HOST_SUFFIXES.some((s) => host === s || host.endsWith(`.${s}`))) {
    return true;
  }
  const user = String(username || "").trim().toLowerCase();
  if (
    user &&
    (host === `${user}.browsable.ai` ||
      host === `${user}.browsable.bio` ||
      host.startsWith(`${user}.`))
  ) {
    return true;
  }
  for (const cd of customDomainHostList(customDomains)) {
    const apex = cd.replace(/^www\./, "");
    if (host === cd || host === apex || host === `www.${apex}` || host.endsWith(`.${apex}`)) {
      return true;
    }
  }
  return false;
}

function shopLinkFromPost(post: unknown): string {
  if (!post || typeof post !== "object") return "";
  const row = post as Record<string, unknown>;
  return (
    normalizeShopUrl(row.shop_link) ||
    normalizeShopUrl(row.shopLink) ||
    normalizeShopUrl(row.shop_url) ||
    ""
  );
}

/** Featured Outfit: shop_link on browsable / searchbubble / mapped custom domains. */
export function postHasCreatorWebsiteShopLink(
  post: unknown,
  opts?: {
    username?: string;
    customDomain?: string | null;
    customDomains?: string | string[] | null;
  },
): boolean {
  const url = shopLinkFromPost(post);
  if (!url) return false;
  const domains = [
    ...customDomainHostList(opts?.customDomains),
    ...customDomainHostList(opts?.customDomain),
  ];
  return isBrowsableShopUrl(url, opts?.username, domains);
}

/** Recent Looks: any non-empty shop_link. */
export function postHasShopLink(post: unknown): boolean {
  return Boolean(shopLinkFromPost(post));
}

export function classifyShopUrl(
  shopUrl: string,
  username: string,
  collections: CreatorCollectionRow[],
  customDomains?: string | string[] | null,
): { kind: LookShopKind; collection: CreatorCollectionRow | null } {
  const url = String(shopUrl || "").trim();
  if (!url) return { kind: "external", collection: null };
  const collection =
    collections.find((c) => shopUrlMatchesCollection(url, c, username)) || null;
  if (collection) return { kind: "collection", collection };
  if (isBrowsableShopUrl(url, username, customDomains)) {
    return { kind: "browsable", collection: null };
  }
  return { kind: "external", collection: null };
}

/** Instagram FEED or REELS (POST is stored as feed). Stories / highlights out. */
export function isInstagramFeedOrReelsType(
  platform: unknown,
  type: unknown,
  formatSlug?: unknown,
): boolean {
  const platformLc = String(platform || "").trim().toLowerCase();
  if (platformLc !== "instagram") return false;
  const typeUc = String(type || "").trim().toUpperCase();
  if (typeUc === "STORY" || typeUc === "HIGHLIGHTS") return false;
  if (INSTAGRAM_LOOK_TYPES.has(typeUc)) return true;
  const format = String(formatSlug || "").trim().toLowerCase();
  if (format === "stories" || format === "story") return false;
  return format === "reels";
}

/** Instagram posts + reels only — stories/highlights are noise for looks. */
export function isInstagramLookPost(post: ShopCatalogContent): boolean {
  return isInstagramFeedOrReelsType(
    post.platformSlug || post.platform,
    post.type,
    post.formatSlug,
  );
}

async function fetchCollectionRows(userId: string): Promise<CreatorCollectionRow[]> {
  const uid = String(userId || "").trim();
  if (!uid) return [];
  const tables = Array.from(
    new Set([
      getComponentsTableName("website-template"),
      getComponentsTableName("landing-page"),
    ]),
  );
  const rows: CreatorCollectionRow[] = [];
  const seen = new Set<string>();
  for (const table of tables) {
    const { data, error } = await supabase
      .from(table)
      .select("id, type, post_ids, unique_id, route_name, name, display_name, status, status_expiry")
      .eq("user_id", uid)
      .neq("status", "deleted");
    if (error || !data) continue;
    for (const raw of data as Record<string, unknown>[]) {
      const status = String(raw.status_expiry ?? raw.status ?? "")
        .toLowerCase()
        .trim();
      if (status === "hidden" || status === "deleted") continue;
      const mapped = mapCollectionRow(raw);
      if (!mapped) continue;
      const key = mapped.slugs.join("|") || mapped.id;
      if (seen.has(key)) continue;
      seen.add(key);
      rows.push(mapped);
    }
  }
  return rows;
}

/**
 * Instagram posts/reels with a shop link — used by Featured Outfit + Recent Looks.
 * Collection-matched links resolve products; external links stay product-empty.
 */
export function useCreatorLooks(args: {
  username: string;
  userId: string;
  enabled?: boolean;
  customDomain?: string | null;
  /**
   * Auto-fetch extra look pages after page 1 (inspector + Content bands).
   * Does not keep `loading` true — page 1 paints first; pages 2–3 append.
   */
  autoWarmPages?: boolean;
  /** Cap look discovery pages (25 × max). Default 1; Content/inspector use 3. */
  maxPages?: number;
  /**
   * When set, only hydrate products for these look posts (Featured Outfit /
   * Recent Looks cfg). Avoids pulling hundreds of unrelated product IDs.
   */
  focusPostIds?: string[] | null;
}) {
  const username = args.username.trim().toLowerCase();
  const userId = args.userId.trim();
  const customDomain = args.customDomain ?? null;
  const enabled = Boolean(args.enabled !== false && username && userId);
  const autoWarmPages = Boolean(args.autoWarmPages);
  const LOOKS_MAX_PAGES = Math.max(1, Math.min(args.maxPages ?? 1, 3));
  const mappedDomainsQuery = useQuery(
    ["creator-mapped-domain-hosts", username, userId],
    () => fetchCreatorMappedDomainHosts({ username, userId }),
    { enabled, staleTime: 60_000, refetchOnWindowFocus: false },
  );
  const mappedDomainHosts = useMemo(
    () =>
      customDomainHostList([
        ...(mappedDomainsQuery.data || []),
        customDomain || "",
      ]),
    [mappedDomainsQuery.data, customDomain],
  );
  const focusKey = (args.focusPostIds || [])
    .map((id) => String(id || "").trim())
    .filter(Boolean)
    .join(",");
  const focusPostIds = useMemo(() => {
    if (!focusKey) return null as string[] | null;
    return focusKey.split(",");
  }, [focusKey]);

  const looksQuery = useInfiniteQuery(
    ["template-creator-looks-rpc-v1", userId, username],
    ({ pageParam = 0 }) =>
      fetchTemplateCreatorLooksRpcPage({
        username,
        userId,
        offset: pageParam,
        limit: TEMPLATE_SHOP_PAGE_SIZE,
      }),
    {
      enabled,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, allPages) => {
        if (allPages.length >= LOOKS_MAX_PAGES) return undefined;
        const loaded = allPages.reduce((n, p) => n + p.items.length, 0);
        if (lastPage.items.length < lastPage.pageSize) return undefined;
        if (loaded >= lastPage.total) return undefined;
        return loaded;
      },
    },
  );

  const lookPostsFromRpc: ShopCatalogContent[] = useMemo(() => {
    const seen = new Set<string>();
    const out: ShopCatalogContent[] = [];
    for (const page of looksQuery.data?.pages ?? []) {
      for (const post of page.items) {
        if (!post.shopUrl || seen.has(post.postId)) continue;
        if (!isInstagramLookPost(post)) continue;
        seen.add(post.postId);
        out.push(post);
      }
    }
    return out;
  }, [looksQuery.data]);

  // Warm extra pages only when requested (inspector), or until focus posts appear.
  const looksPageCount = looksQuery.data?.pages?.length ?? 0;
  const focusMissing = Boolean(
    focusPostIds &&
      focusPostIds.some((id) => !lookPostsFromRpc.some((p) => p.postId === id)),
  );
  useEffect(() => {
    if (!enabled) return;
    if (!looksQuery.hasNextPage || looksQuery.isFetchingNextPage) return;
    if (looksPageCount <= 0 || looksPageCount >= LOOKS_MAX_PAGES) return;
    if (!autoWarmPages && !focusMissing) return;
    void looksQuery.fetchNextPage();
  }, [
    enabled,
    autoWarmPages,
    focusMissing,
    looksPageCount,
    LOOKS_MAX_PAGES,
    looksQuery.hasNextPage,
    looksQuery.isFetchingNextPage,
    looksQuery.fetchNextPage,
  ]);

  // After the looks RPC window is exhausted (or failed), pull any still-missing
  // focus posts by id (Recent Looks / Featured Outfit pins beyond page 3).
  const looksPagesSettled = Boolean(
    !looksQuery.isLoading &&
      !looksQuery.isFetchingNextPage &&
      (looksQuery.isError ||
        (looksPageCount > 0 &&
          (looksPageCount >= LOOKS_MAX_PAGES || !looksQuery.hasNextPage))),
  );
  const focusIdsStillMissing = useMemo(() => {
    if (!focusPostIds?.length || !looksPagesSettled) return [] as string[];
    const have = new Set(lookPostsFromRpc.map((p) => p.postId));
    return focusPostIds.filter((id) => !have.has(id));
  }, [focusPostIds, lookPostsFromRpc, looksPagesSettled]);
  const focusMissingKey = focusIdsStillMissing.join(",");
  const focusBackfillQuery = useQuery(
    ["template-creator-looks-by-ids-v1", userId, username, focusMissingKey],
    () =>
      fetchTemplateCreatorLooksByIds({
        username,
        userId,
        postIds: focusIdsStillMissing,
      }),
    {
      enabled: enabled && focusIdsStillMissing.length > 0,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  );

  const lookPosts: ShopCatalogContent[] = useMemo(() => {
    const seen = new Set(lookPostsFromRpc.map((p) => p.postId));
    const out = [...lookPostsFromRpc];
    for (const post of focusBackfillQuery.data ?? []) {
      if (!post.shopUrl || seen.has(post.postId)) continue;
      if (!isInstagramLookPost(post)) continue;
      seen.add(post.postId);
      out.push(post);
    }
    return out;
  }, [lookPostsFromRpc, focusBackfillQuery.data]);

  /**
   * True until page 1 (and any focus-driven catch-up) settles.
   * Background autoWarmPages must not keep skeletons — Content prioritizes
   * first paint, then warms pages 2–3.
   */
  const looksWarming = Boolean(
    enabled &&
      (looksQuery.isLoading ||
        (focusMissing && looksQuery.isFetchingNextPage) ||
        (focusIdsStillMissing.length > 0 &&
          (focusBackfillQuery.isLoading || focusBackfillQuery.isFetching)) ||
        (looksQuery.isFetching && !looksQuery.data)),
  );

  /** Page 1 settled — safe to unblock deferred Content catalog fetch. */
  const firstPageReady = Boolean(
    !enabled ||
      (!looksQuery.isLoading &&
        (looksQuery.isFetched || looksQuery.isError || looksQuery.data)),
  );

  const collectionsQuery = useQuery(
    ["creator-collection-rows", userId],
    () => fetchCollectionRows(userId),
    { enabled, staleTime: 60_000, refetchOnWindowFocus: false },
  );

  const productIdsByCollection = useMemo(() => {
    const collections = collectionsQuery.data ?? [];
    const byId = new Map(collections.map((c) => [c.id, c]));
    const out = new Map<string, string[]>();
    for (const collection of collections) {
      if (collection.type === "carousel-collection") {
        const nested: string[] = [];
        for (const memberId of collection.postIds) {
          const member = byId.get(memberId);
          if (member?.postIds.length) nested.push(...member.postIds);
        }
        out.set(collection.id, [...new Set(nested)]);
      } else {
        out.set(collection.id, collection.postIds);
      }
    }
    return out;
  }, [collectionsQuery.data]);

  // Only hydrate products for collections linked to *loaded* looks —
  // never every product in every collection (was ~6k IDs → connection death).
  // UI shows ≤24 tiles per look; keep a modest headroom + hard cap.
  const PER_LOOK_PRODUCT_CAP = 32;
  const TOTAL_PRODUCT_HYDRATE_CAP = focusPostIds ? 96 : 400;

  const neededProductIds = useMemo(() => {
    const collections = collectionsQuery.data ?? [];
    if (!lookPosts.length || !collections.length) return [] as string[];
    const hydratePosts = focusPostIds
      ? lookPosts.filter((p) => focusPostIds.includes(p.postId))
      : lookPosts;
    const ids: string[] = [];
    const seen = new Set<string>();
    for (const post of hydratePosts) {
      if (ids.length >= TOTAL_PRODUCT_HYDRATE_CAP) break;
      const { collection } = classifyShopUrl(
        post.shopUrl,
        username,
        collections,
        mappedDomainHosts,
      );
      if (!collection) continue;
      const memberIds =
        productIdsByCollection.get(collection.id) ?? collection.postIds;
      let taken = 0;
      for (const id of memberIds) {
        if (!id || seen.has(id)) continue;
        seen.add(id);
        ids.push(id);
        taken += 1;
        if (
          taken >= PER_LOOK_PRODUCT_CAP ||
          ids.length >= TOTAL_PRODUCT_HYDRATE_CAP
        ) {
          break;
        }
      }
    }
    return ids;
  }, [
    lookPosts,
    collectionsQuery.data,
    productIdsByCollection,
    username,
    mappedDomainHosts,
    focusPostIds,
    TOTAL_PRODUCT_HYDRATE_CAP,
  ]);

  const productsQuery = useQuery(
    [
      "creator-look-products-v2",
      userId,
      username,
      focusKey,
      neededProductIds.length,
      neededProductIds.slice(0, 40).join(","),
    ],
    () =>
      fetchTemplateShopProductsByIds({
        username,
        userId,
        postIds: neededProductIds,
      }),
    {
      enabled: enabled && neededProductIds.length > 0,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  );

  const looks: CreatorLook[] = useMemo(() => {
    const collections = collectionsQuery.data ?? [];
    const productsById = new Map(
      (productsQuery.data ?? []).map((p) => [p.postId, p]),
    );
    const out: CreatorLook[] = [];
    for (const post of lookPosts) {
      const { kind, collection } = classifyShopUrl(
        post.shopUrl,
        username,
        collections,
        mappedDomainHosts,
      );
      const ids = collection
        ? productIdsByCollection.get(collection.id) ?? collection.postIds
        : [];
      out.push({
        post,
        collection,
        shopKind: kind,
        products: ids
          .map((id) => productsById.get(id))
          .filter((p): p is ShopCatalogProduct => Boolean(p)),
      });
    }
    return out;
  }, [
    lookPosts,
    collectionsQuery.data,
    productIdsByCollection,
    productsQuery.data,
    username,
    mappedDomainHosts,
  ]);

  const collectionLooks = useMemo(
    () => looks.filter((l) => l.shopKind === "collection"),
    [looks],
  );

  const browsableLooks = useMemo(
    () =>
      looks.filter((l) =>
        isBrowsableShopUrl(l.post.shopUrl, username, mappedDomainHosts),
      ),
    [looks, username, mappedDomainHosts],
  );

  return {
    looks,
    /** Posts whose shop link matches a creator collection (legacy). */
    collectionLooks,
    /** Featured Outfit: IG feed/reels whose shop_link is on our domains. */
    browsableLooks,
    /** Hosts from domain_mappings (+ optional inspector customDomain). */
    mappedDomainHosts,
    loading:
      looksWarming ||
      (collectionsQuery.isLoading && !collectionsQuery.data),
    /** True once looks RPC page 1 has settled (before background warm pages). */
    firstPageReady,
    /** Product tiles for looks may still be resolving after looks paint. */
    productsLoading:
      neededProductIds.length > 0 &&
      (productsQuery.isLoading || productsQuery.isFetching) &&
      !productsQuery.data,
    collections: collectionsQuery.data ?? [],
  };
}

/** @deprecated Use useCreatorLooks — alias kept for ShopMerchInspector. */
export function useCreatorCollectionLooks(args: {
  username: string;
  userId: string;
  enabled?: boolean;
}) {
  const result = useCreatorLooks(args);
  return {
    looks: result.collectionLooks,
    loading: result.loading,
    collections: result.collections,
  };
}

/** Expand carousel-collection members the same way as useCreatorLooks. */
export function resolveCollectionMemberIds(
  collection: CreatorCollectionRow | null | undefined,
  collections: CreatorCollectionRow[],
): string[] {
  if (!collection) return [];
  if (collection.type === "carousel-collection") {
    const byId = new Map(collections.map((c) => [c.id, c]));
    const nested: string[] = [];
    for (const memberId of collection.postIds) {
      const member = byId.get(memberId);
      if (member?.postIds.length) nested.push(...member.postIds);
    }
    return [...new Set(nested)];
  }
  return collection.postIds;
}

function lookTileImageUrl(row: Record<string, unknown>): string {
  const fromCard = pickFirstNonEmptyImageField(row.card_image);
  if (fromCard && !isLikelyVideoUrl(fromCard)) return fromCard;
  for (const key of ["thumbnail", "thumbnail_url", "image"] as const) {
    const raw = String(row[key] ?? "").trim();
    const first = raw.split(",")[0]?.trim() || "";
    if (first && !isLikelyVideoUrl(first)) return first;
  }
  const media = String(row.media_url ?? "").trim();
  const first = media.split(",")[0]?.trim() || "";
  if (first && !isLikelyVideoUrl(first)) return first;
  return "";
}

function lookTileVideoUrl(row: Record<string, unknown>): string {
  return resolvePlayableVideoUrl({
    mediaUrl: String(row.media_url ?? "").trim(),
    storageUrl: String(row.storage_url ?? "").trim(),
    type: String(row.type ?? "").trim(),
    platform: String(row.platform ?? "").trim(),
  });
}

function lookTileIsVideo(
  _type: string,
  _platform: string,
  _mediaRaw: string,
  videoUrl: string,
): boolean {
  return Boolean(videoUrl);
}

export function mapCollectionComponentToProduct(
  row: Record<string, unknown>,
): ShopCatalogProduct | null {
  const postId = String(row.id ?? row.post_id ?? "").trim();
  if (!postId) return null;
  const mediaRaw = String(row.media_url ?? "").trim();
  const videoUrl = lookTileVideoUrl(row);
  const type = String(row.type ?? "").trim();
  const platform = String(row.platform ?? "").trim();
  const isVideo = lookTileIsVideo(type, platform, mediaRaw, videoUrl);
  const image =
    lookTileImageUrl(row) ||
    (isVideo ? "/assets/dummy_video.png" : "");
  const name = sanitizePublicCardTitle(
    row.product_name ??
      row.content_title ??
      row.display_name ??
      row.name ??
      "",
    {
      ...row,
      username:
        row.username ?? row.user_name ?? row.handle ?? undefined,
    },
    {
      creatorUsername: String(
        row.username ?? row.user_name ?? row.handle ?? "",
      ).trim() || null,
    },
  );
  const permalink = String(row.permalink ?? "").trim();
  const url =
    resolveShopButtonCta(row)?.url ||
    permalink ||
    "#";
  const price = formatLookProductPrice(
    row.price ?? row.product_price ?? row.sale_price,
  );
  const breadcrumbsRaw = String(row.breadcrumbs ?? "").trim();
  return {
    postId,
    name: name || "Product",
    url,
    image,
    contentCategory: String(row.content_category ?? "").trim(),
    platform,
    breadcrumbsRaw,
    breadcrumbs: parseProductBreadcrumbs(breadcrumbsRaw),
    ...(price ? { price } : {}),
    ...(permalink ? { permalink } : {}),
    ...(isVideo && videoUrl ? { isVideo: true, mediaUrl: videoUrl } : {}),
  };
}

function formatLookProductPrice(raw: unknown): string {
  if (raw == null || raw === "") return "";
  if (typeof raw === "number" && Number.isFinite(raw) && raw > 0) {
    return `$${raw % 1 === 0 ? raw.toFixed(0) : raw.toFixed(2)}`;
  }
  const s = String(raw).trim();
  if (!s) return "";
  if (s.startsWith("$")) return s;
  const n = Number(s.replace(/,/g, ""));
  if (Number.isFinite(n) && n > 0) {
    return `$${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)}`;
  }
  return "";
}

/** Twitter / X / LinkedIn / Newsletter — text posts use the black NoMediaPost card. */
export function isLookTextPlatform(platform: string, feedType?: string): boolean {
  const p = String(platform || "").trim().toLowerCase();
  const t = String(feedType || "").trim().toUpperCase();
  if (t === "TWEET" || t === "LINKEDIN" || t === "TWITTER" || t === "NEWSLETTER") {
    return true;
  }
  if (t === "POST" && p === "linkedin") return true;
  return (
    p === "twitter" ||
    p === "linkedin" ||
    p === "x" ||
    p === "newsletter"
  );
}

/**
 * Platform/type key for Group member covers and NoMediaPost cards.
 * Component rows use `type` / `display_name` (not `posts.platform`).
 */
export function platformKeyFromComponentRow(item: {
  platform?: unknown;
  type?: unknown;
  display_name?: unknown;
}): string {
  const platform = String(item?.platform ?? "")
    .trim()
    .toLowerCase();
  if (platform) return platform === "x" ? "twitter" : platform;
  const type = String(item?.type ?? "")
    .trim()
    .toLowerCase();
  const dn = String(item?.display_name ?? "")
    .trim()
    .toLowerCase();
  const key = dn || type;
  if (!key) return "";
  if (key.includes("linkedin")) return "linkedin";
  if (key.includes("twitter") || key === "x") return "twitter";
  if (key.includes("tiktok")) return "tiktok";
  if (key.includes("youtube")) return "youtube";
  if (key.includes("podcast")) return "podcast";
  if (key.includes("newsletter")) return "newsletter";
  if (
    key.includes("instagram story") ||
    key === "story" ||
    key.includes("ig story")
  ) {
    return "instagram story";
  }
  if (key.includes("reel") || key.includes("instagram")) return "instagram";
  if (key.includes("featured")) return "featured";
  return key;
}

/** Face label for imageless cards (matches feed / LiB / Group covers). */
export function textPlatformNoMediaLabel(platform: string): string {
  const p = String(platform || "").trim().toLowerCase();
  if (!p) return "Post";
  if (p === "newsletter") return "Newsletter";
  if (p === "linkedin") return "LinkedIn Post";
  if (p === "twitter" || p === "x") return "X Post";
  if (p === "tiktok") return "TikTok Post";
  if (p === "youtube") return "YouTube Post";
  if (p === "podcast" || p === "latest-podcast") return "Podcast";
  if (p === "instagram story" || p === "story") return "Instagram Story";
  if (p === "reels" || p === "instagram") return "Instagram Post";
  if (p === "featured") return "Featured Post";
  if (p === "all" || p.includes("recent content") || p.includes("content feed")) {
    return "Content Post";
  }
  const label = p
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
  if (!label) return "Post";
  return /\bpost$/i.test(label) ? label : `${label} Post`;
}

function mapFeedRowToLookTile(
  row: Record<string, unknown>,
): ShopCatalogProduct | null {
  const postId = String(row.post_id ?? row.id ?? "").trim();
  if (!postId) return null;
  const mediaRaw = String(row.media_url ?? "").trim();
  const videoUrl = lookTileVideoUrl(row);
  const platform = String(row.platform ?? "").trim();
  const feedType = String(row.type ?? "").trim();
  const isVideo = lookTileIsVideo(feedType, platform, mediaRaw, videoUrl);
  const image =
    lookTileImageUrl(row) ||
    (isVideo ? "/assets/dummy_video.png" : "");
  const name = sanitizePublicCardTitle(
    row.product_name ??
      row.shop_title ??
      row.content_title ??
      // Day-window shoppable posts (LiB "Shop This Past Week") often only have caption.
      row.caption ??
      "",
    row,
  );
  const permalink = String(row.permalink ?? "").trim();
  const url =
    resolveShopButtonCta(row)?.url ||
    permalink ||
    "#";
  const price = formatLookProductPrice(
    row.price ?? row.product_price ?? row.sale_price,
  );
  const isText = !image && !isVideo && isLookTextPlatform(platform, feedType);
  const postDate = String(row.post_date ?? "").trim();
  const breadcrumbsRaw = String(row.breadcrumbs ?? "").trim();
  return {
    postId,
    // Empty when no product_name/caption — catalog cards hide the name row.
    name,
    url,
    image,
    contentCategory: String(row.content_category ?? "").trim(),
    platform,
    breadcrumbsRaw,
    breadcrumbs: parseProductBreadcrumbs(breadcrumbsRaw),
    ...(price ? { price } : {}),
    ...(isText ? { isText: true } : {}),
    ...(postDate ? { postDate } : {}),
    ...(permalink ? { permalink } : {}),
    ...(isVideo && videoUrl ? { isVideo: true, mediaUrl: videoUrl } : {}),
  };
}

type ResolvedShopLinkComponent = {
  id: string;
  type: string;
  name: string;
  displayName: string;
  postIds: string[];
  contentOrder: string;
  customOrder: string[];
  query: string;
  details: string;
  removedPostIds: string;
  shoppableContentFilter: string;
  shoppableStatusFilter: string;
  pinnedPosts: string[];
  from: "landing-page" | "website-template" | "embed-search";
  raw: Record<string, unknown>;
};

type ShopLinkLoadMode =
  | "custom-content"
  | "custom-links"
  | "carousel"
  | "query-content"
  | "query-links"
  | "trending"
  | "type-content"
  | "yesterday-links"
  | "yesterday-content"
  | "none";

const DAY_CONFIGS: Record<string, number[]> = {
  Yesterday: [1],
  "This Week": [7],
  "This Week Content": [7],
  "This Month": [30],
};

const TYPE_CONTENT_BY_TYPE: Record<string, { platform: string; feedType: string }> = {
  featured: { platform: "featured", feedType: "FEATURE" },
  reels: { platform: "instagram", feedType: "REELS" },
  // Mixed platforms (Recent Content → Platform: All).
  all: { platform: "all", feedType: "ALL" },
  // Platform carousels are keyed by `type` (display_name may be customized).
  linkedin: { platform: "linkedin", feedType: "POST" },
  youtube: { platform: "youtube", feedType: "VIDEO" },
  tiktok: { platform: "tiktok", feedType: "TIKTOK" },
  twitter: { platform: "twitter", feedType: "TWEET" },
  podcast: { platform: "podcast", feedType: "PODCAST" },
};

const TYPE_CONTENT_BY_DISPLAY: Record<
  string,
  { platform: string; feedType: string }
> = {
  "instagram story": { platform: "instagram", feedType: "STORY" },
  all: { platform: "all", feedType: "ALL" },
  youtube: { platform: "youtube", feedType: "VIDEO" },
  tiktok: { platform: "tiktok", feedType: "TIKTOK" },
  linkedin: { platform: "linkedin", feedType: "POST" },
  twitter: { platform: "twitter", feedType: "TWEET" },
  podcast: { platform: "podcast", feedType: "PODCAST" },
};

function resolveShopLinkLoadMode(
  type: string,
  displayName: string,
): {
  mode: ShopLinkLoadMode;
  typePlatform: string;
  typeFeed: string;
  dayNumbers: number[] | null;
  daywise: boolean;
} {
  const t = String(type || "").trim().toLowerCase();
  const d = String(displayName || "").trim().toLowerCase();
  const dayMappings = StaticVariables().dayMappings as Record<string, number[]>;

  if (d === "this week content") {
    return {
      mode: "yesterday-content",
      typePlatform: "",
      typeFeed: "",
      dayNumbers: DAY_CONFIGS["This Week Content"],
      daywise: false,
    };
  }
  if (DAY_CONFIGS[displayName as keyof typeof DAY_CONFIGS] || dayMappings?.[displayName]) {
    return {
      mode: "yesterday-links",
      typePlatform: "",
      typeFeed: "",
      dayNumbers:
        DAY_CONFIGS[displayName as keyof typeof DAY_CONFIGS] ||
        dayMappings?.[displayName] ||
        [],
      daywise: Boolean(dayMappings?.[displayName]),
    };
  }
  if (t === "carousel-collection") {
    return { mode: "carousel", typePlatform: "", typeFeed: "", dayNumbers: null, daywise: false };
  }
  if (t === "content") {
    return { mode: "custom-content", typePlatform: "", typeFeed: "", dayNumbers: null, daywise: false };
  }
  if (t === "links") {
    return { mode: "custom-links", typePlatform: "", typeFeed: "", dayNumbers: null, daywise: false };
  }
  if (t === "query-content" || t === "content-series") {
    return { mode: "query-content", typePlatform: "", typeFeed: "", dayNumbers: null, daywise: false };
  }
  if (t === "query-links") {
    return { mode: "query-links", typePlatform: "", typeFeed: "", dayNumbers: null, daywise: false };
  }
  // Seller / products-carousel: don't mis-route into custom-content. Empty
  // products → TemplateCollectionCatalogPanel falls back to StayUpToDate.
  if (t === "products-carousel") {
    return { mode: "none", typePlatform: "", typeFeed: "", dayNumbers: null, daywise: false };
  }
  if (t === "trending products" || t === "trending content") {
    return { mode: "trending", typePlatform: "", typeFeed: "", dayNumbers: null, daywise: false };
  }
  if (TYPE_CONTENT_BY_TYPE[t]) {
    return {
      mode: "type-content",
      typePlatform: TYPE_CONTENT_BY_TYPE[t].platform,
      typeFeed: TYPE_CONTENT_BY_TYPE[t].feedType,
      dayNumbers: null,
      daywise: false,
    };
  }
  if (TYPE_CONTENT_BY_DISPLAY[d]) {
    return {
      mode: "type-content",
      typePlatform: TYPE_CONTENT_BY_DISPLAY[d].platform,
      typeFeed: TYPE_CONTENT_BY_DISPLAY[d].feedType,
      dayNumbers: null,
      daywise: false,
    };
  }
  // Default: treat like hand-picked content (same as CustomContentDataLoader).
  return { mode: "custom-content", typePlatform: "", typeFeed: "", dayNumbers: null, daywise: false };
}

/**
 * Normalize feed rows from react-query infinite `{ pages }` **or** already-
 * flattened arrays (Yesterday / Trending hooks replace `data` with row[]).
 */
function flattenInfinitePages(data: unknown): Record<string, unknown>[] {
  if (!data) return [];
  if (Array.isArray(data)) {
    return data as Record<string, unknown>[];
  }
  const pages = (data as { pages?: unknown }).pages;
  if (!Array.isArray(pages)) return [];
  const out: Record<string, unknown>[] = [];
  for (const page of pages) {
    if (Array.isArray(page)) {
      out.push(...(page as Record<string, unknown>[]));
      continue;
    }
    if (page && typeof page === "object" && Array.isArray((page as any).data)) {
      out.push(...((page as { data: Record<string, unknown>[] }).data));
    }
  }
  return out;
}

function fromForTable(
  table: string,
): ResolvedShopLinkComponent["from"] {
  if (table === "template_components") return WEBSITE_TEMPLATE_SURFACE;
  if (table === "embed_components") return "embed-search";
  return "landing-page";
}

function slugifyLabel(label: string): string {
  return String(label || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function rowMatchesShopSlugCi(
  row: Record<string, unknown>,
  slug: string,
): boolean {
  const want = String(slug || "")
    .trim()
    .replace(/^\/+|\/+$/g, "")
    .toLowerCase();
  if (!want) return false;
  for (const raw of [
    row.route_name,
    row.unique_id,
    row.name,
    row.display_name,
  ]) {
    const value = String(raw || "")
      .trim()
      .replace(/^\/+|\/+$/g, "")
      .toLowerCase();
    if (!value) continue;
    if (value === want) return true;
    const last = value.split("/").filter(Boolean).pop() || "";
    if (last === want) return true;
    if (slugifyLabel(String(raw || "")) === want) return true;
  }
  return false;
}

function mapResolvedComponent(
  row: Record<string, unknown>,
  from: ResolvedShopLinkComponent["from"],
  fallbackName: string,
): ResolvedShopLinkComponent {
  const customOrder = parseComponentIdList(row.custom_order);
  const postIds = parseComponentIdList(row.post_ids);
  const pinnedPosts = parseComponentIdList(row.pinned_posts);
  return {
    id: String(row.id ?? "").trim(),
    type: String(row.type ?? "").trim().toLowerCase(),
    name:
      String(row.name ?? row.display_name ?? fallbackName).trim() || fallbackName,
    displayName:
      String(row.display_name ?? row.name ?? fallbackName).trim() || fallbackName,
    postIds,
    contentOrder: String(row.content_order ?? "DESC").trim() || "DESC",
    customOrder,
    query: String(row.query ?? "").trim(),
    details: String(row.details ?? "").trim(),
    removedPostIds: String(row.removed_postids ?? "").trim(),
    shoppableContentFilter: String(
      row.shoppable_content_filter ?? "both",
    ).trim() || "both",
    shoppableStatusFilter: String(
      row.shoppable_status_filter ?? "all",
    ).trim() || "all",
    pinnedPosts,
    from,
    raw: row,
  };
}

function parseComponentIdList(raw: unknown): string[] {
  if (raw == null || raw === "") return [];
  if (Array.isArray(raw)) {
    return raw.map((v) => String(v ?? "").trim()).filter(Boolean);
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.map((v) => String(v ?? "").trim()).filter(Boolean);
        }
      } catch {
        /* fall through */
      }
    }
    return normalizeCustomContentPostIds(trimmed);
  }
  return normalizeCustomContentPostIds(raw);
}

async function queryComponentBySlugDirect(
  table: string,
  userId: string,
  username: string,
  slug: string,
): Promise<Record<string, unknown> | null> {
  const variants = Array.from(
    new Set(
      [slug, slug.toLowerCase(), slug.toUpperCase()]
        .map((v) => v.trim())
        .filter(Boolean),
    ),
  );

  const scoped = () => {
    let q = supabase.from(table).select("*").neq("status", "deleted");
    if (userId) q = q.eq("user_id", userId);
    else if (username) q = q.eq("username", username);
    return q;
  };

  // Fast path: exact route_name / unique_id (same as useLoadRenderComponent).
  for (const variant of variants) {
    const { data } = await scoped()
      .or(`unique_id.eq.${variant},route_name.eq.${variant}`)
      .limit(5);
    const hit = (data as Record<string, unknown>[] | null)?.find((row) =>
      rowMatchesShopSlugCi(row, slug),
    );
    if (hit) return hit;
  }

  // Case-insensitive / name match.
  const { data: fuzzy } = await scoped()
    .or(
      `route_name.ilike.${slug},unique_id.ilike.${slug},name.ilike.${slug},display_name.ilike.${slug}`,
    )
    .limit(20);
  const fuzzyHit = (fuzzy as Record<string, unknown>[] | null)?.find((row) =>
    rowMatchesShopSlugCi(row, slug),
  );
  if (fuzzyHit) return fuzzyHit;

  // Username fallback when user_id rows miss (legacy rows).
  if (username && userId) {
    const { data: byUsername } = await supabase
      .from(table)
      .select("*")
      .eq("username", username)
      .neq("status", "deleted");
    const named = (byUsername as Record<string, unknown>[] | null)?.find(
      (row) => rowMatchesShopSlugCi(row, slug),
    );
    if (named) return named;
  }

  // Last resort: scan owner rows.
  const { data: all } = await scoped();
  const scanned = (all as Record<string, unknown>[] | null)?.find((row) =>
    rowMatchesShopSlugCi(row, slug),
  );
  return scanned || null;
}

/**
 * Resolve the landing/individual page component behind a shop_link slug —
 * same surfaces IndividualComponentLoader / useLoadRenderComponent use.
 */
async function resolveShopLinkComponent(args: {
  userId: string;
  username: string;
  slug: string;
}): Promise<ResolvedShopLinkComponent | null> {
  const slug = String(args.slug || "").trim();
  if (!slug) return null;
  if (!args.userId && !args.username) return null;

  // 1) Canonical public lookup (FollowerView uses from=undefined).
  try {
    const publicRow = await fetchStayUpToDateComponentRow(
      args.userId,
      slug,
      undefined,
      supabase,
      args.username,
    );
    if (publicRow) {
      return mapResolvedComponent(
        publicRow as Record<string, unknown>,
        "landing-page",
        slug,
      );
    }
  } catch {
    /* continue with direct table queries */
  }

  // 2) Direct table queries — more resilient to casing / name-only routes.
  const tables = [
    "stayuptodate_components",
    "template_components",
    "embed_components",
  ] as const;
  for (const table of tables) {
    try {
      const row = await queryComponentBySlugDirect(
        table,
        args.userId,
        args.username,
        slug,
      );
      if (row) {
        return mapResolvedComponent(row, fromForTable(table), slug);
      }
    } catch {
      /* try next table */
    }
  }

  // 3) Website-template dedicated helper.
  try {
    const template = await fetchStayUpToDateComponentRow(
      args.userId,
      slug,
      WEBSITE_TEMPLATE_SURFACE,
      supabase,
      args.username,
    );
    if (template) {
      return mapResolvedComponent(
        template as Record<string, unknown>,
        WEBSITE_TEMPLATE_SURFACE,
        slug,
      );
    }
  } catch {
    /* ignore */
  }

  return null;
}

async function fetchPostShopUrl(args: {
  username: string;
  userId: string;
  postId: string;
}): Promise<string> {
  const postId = String(args.postId || "").trim();
  const username = args.username.trim().toLowerCase();
  if (!postId || !username || !args.userId) return "";
  const { data, error } = await supabase
    .from(username)
    .select("shop_link, tagged_link, external_link")
    .eq("user_id", args.userId)
    .eq("post_id", postId)
    .maybeSingle();
  if (error || !data) return "";
  return resolvePostShopUrl(data as Record<string, unknown>);
}

function rowsFromCustomContentData(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  if (data && typeof data === "object" && Array.isArray((data as any).data)) {
    return (data as { data: Record<string, unknown>[] }).data;
  }
  return [];
}

function mapFeedRowsToCatalogContent(
  rows: Record<string, unknown>[],
): ShopCatalogContent[] {
  const seen = new Set<string>();
  const out: ShopCatalogContent[] = [];
  for (const row of rows) {
    const item = mapFeedPostToShopCatalogContent(row);
    if (!item || seen.has(item.postId)) continue;
    seen.add(item.postId);
    out.push(item);
  }
  return out;
}

/**
 * Posts under a shop_link — mirrors IndividualComponentLoader type routing:
 * content/links, carousel-collection, query-*, trending, featured/platform
 * feeds, and day-window sections.
 */
export function useShopLinkIndividualPosts(args: {
  shopUrl: string;
  username: string;
  userId: string;
  /**
   * When set, always re-read shop_link from this post (source of truth) so
   * catalog/cache can’t keep an old caroline/external URL after an edit.
   */
  postId?: string;
  enabled?: boolean;
  /**
   * Content-page collection masonry only. Looks / Products keep the default
   * product mapping and loading — do not turn this on there.
   */
  includeContent?: boolean;
}) {
  const includeContent = args.includeContent === true;
  const username = args.username.trim().toLowerCase();
  const userId = args.userId.trim();
  const postId = String(args.postId || "").trim();
  const shopUrlArg = String(args.shopUrl || "").trim();

  const shopUrlQuery = useQuery(
    ["look-post-shop-url", userId, username, postId],
    () => fetchPostShopUrl({ username, userId, postId }),
    {
      enabled: Boolean(
        args.enabled !== false && username && userId && postId,
      ),
      staleTime: 5_000,
      refetchOnWindowFocus: true,
    },
  );

  // Prefer live post shop_link when we have a postId; fall back to catalog URL.
  const shopUrl =
    (postId ? String(shopUrlQuery.data || "").trim() : "") ||
    shopUrlArg ||
    "";
  const slug = collectionSlugFromShopUrl(shopUrl, username);
  const waitingOnShopUrl = Boolean(
    postId && shopUrlQuery.isLoading && !shopUrlQuery.data && !shopUrlArg,
  );
  const enabled = Boolean(
    args.enabled !== false &&
      !waitingOnShopUrl &&
      slug &&
      username &&
      (userId || username),
  );

  const componentQuery = useQuery(
    ["shop-link-individual-component-v2", userId, username, slug || ""],
    () =>
      resolveShopLinkComponent({
        userId,
        username,
        slug: slug || "",
      }),
    {
      enabled,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  );

  const component = componentQuery.data ?? null;
  const type = component?.type || "";
  const displayName = component?.displayName || "";
  const load = resolveShopLinkLoadMode(type, displayName);
  const mode = enabled && component ? load.mode : "none";

  // Hand-picked ids: prefer post_ids, fall back to custom_order (same lists
  // IndividualComponentLoader ultimately feeds into content loaders).
  const memberIds = useMemo(() => {
    if (component?.postIds?.length) return component.postIds;
    if (component?.customOrder?.length) return component.customOrder;
    return EMPTY_POST_IDS;
  }, [component?.postIds, component?.customOrder]);
  const title =
    enabled && component
      ? component.name || component.displayName || slug || "look"
      : "";
  const contentOrder = component?.contentOrder || "DESC";
  const customOrder = useMemo(() => {
    if (component?.customOrder?.length) return component.customOrder;
    return memberIds;
  }, [component?.customOrder, memberIds]);
  const removedPostIds = component?.removedPostIds || "";
  const queryTerm = component?.query || "";
  const details = component?.details || "";
  const detailsParts = details.split(",");
  const shoppableFilter = component?.shoppableContentFilter || "both";
  const shoppableStatus = component?.shoppableStatusFilter || "all";
  const pinnedPosts = component?.pinnedPosts || [];
  const contentDateWindow = useMemo(
    () =>
      getContentFeedDateWindowFromComponent({
        details: component?.raw?.details ?? details,
        id: component?.id,
      }),
    [component?.raw?.details, component?.id, details],
  );

  const platformFilter = readPlatformFilterFromDetails(
    component?.raw?.details ?? details,
  );
  // Match QueryContentDataLoader: stale products filter on content keywords
  // must not run the shop keyword RPC.
  const queryShowFilter = (() => {
    const raw = (shoppableFilter || "both").toLowerCase() || "both";
    if (mode === "query-content" && raw === "products") return "content" as ShowFilter;
    return raw as ShowFilter;
  })();
  const querySocials = deriveAllowedPlatforms(
    platformFilter,
    queryShowFilter,
    mode === "query-links",
  );
  const defaultContentOrder = StaticVariables().DEFAULT_CONTENT_ORDER;

  const useCustomIds =
    mode === "custom-content" || mode === "custom-links"
      ? memberIds
      : EMPTY_POST_IDS;
  const useCarouselIds = mode === "carousel" ? memberIds : EMPTY_POST_IDS;
  const runQuery = mode === "query-content" || mode === "query-links";
  const runTypeContent = mode === "type-content";
  const runTrending = mode === "trending";
  const runYesterdayLinks = mode === "yesterday-links";
  const runYesterdayContent = mode === "yesterday-content";

  const customContent = useLoadCustomContent(
    username,
    userId,
    useCustomIds,
    mode === "custom-content" || mode === "custom-links" ? title : "",
    contentOrder,
    customOrder,
    mode === "custom-links" ? shoppableFilter : undefined,
    undefined,
    undefined,
    mode === "custom-content" ? shoppableStatus : "all",
    mode === "custom-content" || mode === "custom-links",
  );

  const collectionComponents = useLoadCollectionComponents(
    username,
    userId,
    useCarouselIds,
    mode === "carousel" ? title : "",
    contentOrder || "CUSTOM",
    customOrder,
    undefined,
    component?.from || "landing-page",
  );

  const queryFeed = useLoadQueryFeed(
    username,
    userId,
    runQuery ? queryTerm : "",
    querySocials,
    defaultContentOrder,
    mode === "query-links",
    removedPostIds,
    runQuery ? title : "",
    contentOrder,
    customOrder,
  );

  const typeContent = useLoadTypeContentData(
    username,
    userId,
    load.typePlatform || "featured",
    load.typeFeed || "FEATURE",
    removedPostIds,
    contentOrder,
    customOrder,
    shoppableFilter,
    pinnedPosts,
    shoppableStatus,
    "default",
    slug || "look",
    runTypeContent && Boolean(title),
    contentDateWindow,
  );

  const trending = useLoadTrendingProducts(
    userId,
    username,
    runTrending ? title : "",
    detailsParts[0],
    removedPostIds,
    detailsParts[1],
    detailsParts[2],
    detailsParts[3],
    contentOrder || "DESC",
    type,
    runTrending && Boolean(title),
    customOrder,
    undefined,
    false,
    trendingClickSource({ from: component?.from, type }),
  );

  // Same filter as YesterdayLinksLoader / LiB — honor DB shoppable_content_filter.
  // Hardcoding "products" made content day-windows (e.g. Caroline "Shop This Past
  // Week") load product-bank rows instead of the shoppable posts the LiB strip shows.
  const yesterdayLinksFilter = (() => {
    if (!runYesterdayLinks) return undefined;
    const f = String(shoppableFilter || "")
      .trim()
      .toLowerCase();
    if (f === "content" || f === "products") return f;
    return "both";
  })();

  const yesterdayLinks = useLoadYesterdayLinks(
    username,
    userId,
    runYesterdayLinks ? load.dayNumbers || EMPTY_DAY_NUMBERS : EMPTY_DAY_NUMBERS,
    memberIds,
    removedPostIds,
    runYesterdayLinks ? title : "",
    undefined,
    contentOrder,
    undefined,
    load.daywise ? "daywise" : "",
    customOrder,
    yesterdayLinksFilter,
    "default",
    slug || "look",
    runYesterdayLinks && Boolean(title),
  );

  const yesterdayContent = useLoadYesterdayContent(
    username,
    userId,
    runYesterdayContent ? load.dayNumbers || [7] : null,
    memberIds,
    removedPostIds,
    runYesterdayContent ? title : "",
    undefined,
    contentOrder,
    customOrder,
  );

  const customProducts = useMemo(
    () =>
      rowsFromCustomContentData(customContent.data)
        .map((row) => mapFeedRowToLookTile(row))
        .filter((p): p is ShopCatalogProduct => Boolean(p)),
    [customContent.data],
  );

  const collectionProducts = useMemo(() => {
    const rows = (
      collectionComponents.data as { data?: Record<string, unknown>[] } | undefined
    )?.data;
    if (!Array.isArray(rows)) return [] as ShopCatalogProduct[];
    return rows
      .map((row) => mapCollectionComponentToProduct(row))
      .filter((p): p is ShopCatalogProduct => Boolean(p));
  }, [collectionComponents.data]);

  const queryProducts = useMemo(() => {
    const pages = queryFeed.data as unknown;
    // useLoadQueryFeed may return pages array directly or { pages }.
    const rows = Array.isArray(pages)
      ? (pages as any[]).flatMap((page) =>
          Array.isArray(page?.data)
            ? page.data
            : Array.isArray(page)
              ? page
              : [],
        )
      : flattenInfinitePages(pages);
    return rows
      .map((row) => mapFeedRowToLookTile(row))
      .filter((p): p is ShopCatalogProduct => Boolean(p));
  }, [queryFeed.data]);

  const typeProducts = useMemo(
    () =>
      flattenInfinitePages(typeContent.data)
        .map((row) => mapFeedRowToLookTile(row))
        .filter((p): p is ShopCatalogProduct => Boolean(p)),
    [typeContent.data],
  );

  const trendingProducts = useMemo(() => {
    const rows = Array.isArray(trending.data)
      ? (trending.data as Record<string, unknown>[])
      : flattenInfinitePages(trending.data);
    return rows
      .map((row) => mapFeedRowToLookTile(row))
      .filter((p): p is ShopCatalogProduct => Boolean(p));
  }, [trending.data]);

  const yesterdayLinkProducts = useMemo(
    () =>
      flattenInfinitePages(yesterdayLinks.data)
        .map((row) => mapFeedRowToLookTile(row))
        .filter((p): p is ShopCatalogProduct => Boolean(p)),
    [yesterdayLinks.data],
  );

  const yesterdayContentProducts = useMemo(
    () =>
      flattenInfinitePages(yesterdayContent.data)
        .map((row) => mapFeedRowToLookTile(row))
        .filter((p): p is ShopCatalogProduct => Boolean(p)),
    [yesterdayContent.data],
  );

  const customContentItems = useMemo(
    () =>
      includeContent
        ? mapFeedRowsToCatalogContent(
            rowsFromCustomContentData(customContent.data),
          )
        : EMPTY_CATALOG_CONTENT,
    [includeContent, customContent.data],
  );
  const collectionContentItems = useMemo(() => {
    if (!includeContent) return EMPTY_CATALOG_CONTENT;
    const rows = (
      collectionComponents.data as { data?: Record<string, unknown>[] } | undefined
    )?.data;
    return mapFeedRowsToCatalogContent(Array.isArray(rows) ? rows : []);
  }, [includeContent, collectionComponents.data]);
  const queryContentItems = useMemo(() => {
    if (!includeContent) return EMPTY_CATALOG_CONTENT;
    const pages = queryFeed.data as unknown;
    const rows = Array.isArray(pages)
      ? (pages as any[]).flatMap((page) =>
          Array.isArray(page?.data)
            ? page.data
            : Array.isArray(page)
              ? page
              : [],
        )
      : flattenInfinitePages(pages);
    return mapFeedRowsToCatalogContent(rows);
  }, [includeContent, queryFeed.data]);
  const typeContentItems = useMemo(
    () =>
      includeContent
        ? mapFeedRowsToCatalogContent(flattenInfinitePages(typeContent.data))
        : EMPTY_CATALOG_CONTENT,
    [includeContent, typeContent.data],
  );
  const trendingContentItems = useMemo(() => {
    if (!includeContent) return EMPTY_CATALOG_CONTENT;
    const rows = Array.isArray(trending.data)
      ? (trending.data as Record<string, unknown>[])
      : flattenInfinitePages(trending.data);
    return mapFeedRowsToCatalogContent(rows);
  }, [includeContent, trending.data]);
  const yesterdayLinkContentItems = useMemo(
    () =>
      includeContent
        ? mapFeedRowsToCatalogContent(flattenInfinitePages(yesterdayLinks.data))
        : EMPTY_CATALOG_CONTENT,
    [includeContent, yesterdayLinks.data],
  );
  const yesterdayContentItems = useMemo(
    () =>
      includeContent
        ? mapFeedRowsToCatalogContent(flattenInfinitePages(yesterdayContent.data))
        : EMPTY_CATALOG_CONTENT,
    [includeContent, yesterdayContent.data],
  );

  const products = useMemo(() => {
    const raw = (() => {
      switch (mode) {
        case "carousel":
          return collectionProducts.length
            ? collectionProducts
            : customProducts;
        case "custom-links":
        case "custom-content":
          return customProducts.length ? customProducts : collectionProducts;
        case "query-content":
        case "query-links":
          return queryProducts;
        case "type-content":
          return typeProducts;
        case "trending":
          return trendingProducts;
        case "yesterday-links":
          return yesterdayLinkProducts;
        case "yesterday-content":
          return yesterdayContentProducts;
        default:
          return [] as ShopCatalogProduct[];
      }
    })();
    // Collections sometimes repeat the same post_id; keep first only so
    // drawer / product rows don't hit duplicate React keys.
    const seen = new Set<string>();
    const out: ShopCatalogProduct[] = [];
    for (const p of raw) {
      const id = String(p.postId || "").trim();
      if (id) {
        if (seen.has(id)) continue;
        seen.add(id);
      }
      out.push(p);
    }
    return out;
  }, [
    mode,
    collectionProducts,
    customProducts,
    queryProducts,
    typeProducts,
    trendingProducts,
    yesterdayLinkProducts,
    yesterdayContentProducts,
  ]);

  const content = useMemo(() => {
    if (!includeContent) return EMPTY_CATALOG_CONTENT;
    const raw = (() => {
      switch (mode) {
        case "carousel":
          return collectionContentItems.length
            ? collectionContentItems
            : customContentItems;
        case "custom-links":
        case "custom-content":
          return customContentItems.length
            ? customContentItems
            : collectionContentItems;
        case "query-content":
        case "query-links":
          return queryContentItems;
        case "type-content":
          return typeContentItems;
        case "trending":
          return trendingContentItems;
        case "yesterday-links":
          return yesterdayLinkContentItems;
        case "yesterday-content":
          return yesterdayContentItems;
        default:
          return EMPTY_CATALOG_CONTENT;
      }
    })();
    const seen = new Set<string>();
    const out: ShopCatalogContent[] = [];
    for (const item of raw) {
      const id = String(item.postId || "").trim();
      if (id) {
        if (seen.has(id)) continue;
        seen.add(id);
      }
      out.push(item);
    }
    return out;
  }, [
    includeContent,
    mode,
    collectionContentItems,
    customContentItems,
    queryContentItems,
    typeContentItems,
    trendingContentItems,
    yesterdayLinkContentItems,
    yesterdayContentItems,
  ]);

  const feedLoading =
    (mode === "custom-content" || mode === "custom-links"
      ? customContent.isLoading || customContent.isFetching
      : false) ||
    (mode === "carousel"
      ? collectionComponents.isLoading || collectionComponents.isFetching
      : false) ||
    (runQuery ? queryFeed.isLoading || queryFeed.isFetching : false) ||
    (runTypeContent ? typeContent.isLoading || typeContent.isFetching : false) ||
    (runTrending ? trending.isLoading || trending.isFetching : false) ||
    (runYesterdayLinks
      ? yesterdayLinks.isLoading || yesterdayLinks.isFetching
      : false) ||
    (runYesterdayContent
      ? yesterdayContent.isLoading || yesterdayContent.isFetching
      : false);

  // Hold loading across shop-url → component → feed handoffs so callers
  // never flash empty between query stages.
  const awaitingShopUrl = Boolean(
    args.enabled !== false &&
      postId &&
      !shopUrl &&
      (shopUrlQuery.isLoading || shopUrlQuery.isFetching),
  );
  const awaitingComponent = Boolean(
    args.enabled !== false &&
      slug &&
      !waitingOnShopUrl &&
      (componentQuery.isLoading ||
        componentQuery.isFetching ||
        (enabled &&
          !componentQuery.data &&
          !componentQuery.isError &&
          componentQuery.status !== "success")),
  );
  const awaitingFeed = Boolean(
    enabled &&
      component &&
      products.length === 0 &&
      (mode === "none" || feedLoading),
  );

  const loading =
    awaitingShopUrl ||
    awaitingComponent ||
    awaitingFeed ||
    (Boolean(postId) && shopUrlQuery.isLoading && !shopUrl) ||
    (enabled &&
      (shopUrlQuery.isLoading ||
        componentQuery.isLoading ||
        (Boolean(component) && products.length === 0 && feedLoading)));

  const pagingSource =
    mode === "trending"
      ? trending
      : runQuery
        ? queryFeed
        : runTypeContent
          ? typeContent
          : runYesterdayLinks
            ? yesterdayLinks
            : runYesterdayContent
              ? yesterdayContent
              : null;

  const hasMore = Boolean(
    pagingSource &&
      "hasNextPage" in pagingSource &&
      pagingSource.hasNextPage,
  );
  const loadingMore = Boolean(
    pagingSource &&
      "isFetchingNextPage" in pagingSource &&
      pagingSource.isFetchingNextPage,
  );
  const fetchMore = () => {
    if (
      pagingSource &&
      "fetchNextPage" in pagingSource &&
      typeof pagingSource.fetchNextPage === "function"
    ) {
      void pagingSource.fetchNextPage();
    }
  };

  return {
    products,
    content,
    loading: Boolean(
      loading &&
        products.length === 0 &&
        (!includeContent || content.length === 0),
    ),
    hasMore,
    loadingMore,
    fetchMore,
    component,
    memberIds,
    slug: slug || "",
    shopUrl,
    loadMode: mode,
  };
}

/**
 * @deprecated Prefer useShopLinkIndividualPosts — kept for call sites that
 * already have a matched CreatorCollectionRow.
 */
export function useCollectionLookProducts(args: {
  collection: CreatorCollectionRow | null | undefined;
  collections: CreatorCollectionRow[];
  username: string;
  userId: string;
  enabled?: boolean;
  /** When set, resolve + load like the individual landing page for this URL. */
  shopUrl?: string;
}) {
  const shopUrl =
    args.shopUrl ||
    (args.collection
      ? `https://browsable.ai/${args.username}/${args.collection.slugs[0] || ""}`
      : "");
  return useShopLinkIndividualPosts({
    shopUrl,
    username: args.username,
    userId: args.userId,
    enabled: Boolean(
      args.enabled !== false && (args.shopUrl || args.collection),
    ),
  });
}
